import React from 'react';
// import { Link } from 'react-router-dom'; // Link seems unused here, can be removed if not needed
// Import new icon components
import HeartIcon from './icons/HeartIcon';
import PoopIcon from './icons/PoopIcon';
import FlameIcon from './icons/FlameIcon';
import SnowflakeIcon from './icons/SnowflakeIcon';

// Helper function: generateGradient (Keep as is)
const generateGradient = (segments) => {
    const colorMap = {
        Performance: '#22c55e', Waiver: '#3b82f6', Injury: '#ef4444',
        Trade: '#eab308', Rankings: '#a855f7', High: '#10b981',
        Medium: '#6366f1', Low: '#f43f5e', Unknown: '#a1a1aa',
        // Add 'Neutral' if needed
         Neutral: '#6b7280', // Example gray for Neutral
    };
    if (!segments || segments.length === 0) { return 'linear-gradient(to right, transparent 0%, transparent 100%)'; }
    let gradientString = 'linear-gradient(to right, ';
    let cumulativePercentage = 0;
    segments.forEach((segment, index) => {
        const start = cumulativePercentage;
        const end = cumulativePercentage + segment.percentage;
        // Ensure segment.key exists before accessing colorMap
        const cssColor = segment.key && colorMap[segment.key] ? colorMap[segment.key] : colorMap['Unknown'];
        gradientString += `${cssColor} ${start.toFixed(2)}%, ${cssColor} ${end.toFixed(2)}%`;
        if (index < segments.length - 1) { gradientString += ', '; }
        cumulativePercentage = end;
    });
    // Simplified handling for filling remaining space
     if (cumulativePercentage < 100 && segments.length > 0) {
         const lastSegment = segments[segments.length - 1];
         const cssColor = lastSegment.key && colorMap[lastSegment.key] ? colorMap[lastSegment.key] : colorMap['Unknown'];
         // Extend the last color to 100% only if needed
         if (cumulativePercentage < 99.9) { // Use a threshold to avoid tiny slivers
             gradientString += `, ${cssColor} ${cumulativePercentage.toFixed(2)}%, ${cssColor} 100%`;
         }
     } else if (segments.length === 0) {
        // Handle case with no segments - maybe show a default background?
        gradientString += `${colorMap['Unknown']} 0%, ${colorMap['Unknown']} 100%`;
     }

    gradientString += ')';
    return gradientString;
};


// Helper function: calculateNpsInfo (Keep as is)
const calculateNpsInfo = (distribution, total) => {
    // Default values
    let color = 'bg-gray-500'; // Neutral gray for default or non-scorable
    let textColor = 'text-white';
    let badgeText = null;
    let details = 'N/A';
    let iconType = null; // Will hold 'heart', 'poop', or null

    if (total === 0) {
        return { score: 0, color, textColor, details, badgeText, iconType };
    }

    const positiveCount = distribution['Positive'] || 0;
    const negativeCount = distribution['Negative'] || 0;
    const neutralCount = distribution['Neutral'] || 0;
    const relevantTotal = positiveCount + negativeCount + neutralCount;

    if (relevantTotal === 0) {
        details = 'No scorable sentiment data.';
        return { score: 0, color, textColor, details, badgeText, iconType };
    }

    const score = Math.round(((positiveCount / relevantTotal) - (negativeCount / relevantTotal)) * 100);
    details = `Pos: ${((positiveCount/total)*100).toFixed(0)}%, Neg: ${((negativeCount/total)*100).toFixed(0)}%, Neu: ${((neutralCount/total)*100).toFixed(0)}%`;

    if (score > 34) {
        color = 'text-pink-500'; // Icon color, background will be transparent or the icon shape
        textColor = 'text-white'; // For score text on top of icon
        badgeText = 'LOVE';
        iconType = 'heart';
    } else if (score >= 0 && score <= 34) {
        color = 'bg-green-500';
        textColor = 'text-white';
    } else if (score < 0 && score > -35) {
        color = 'bg-red-500';
        textColor = 'text-white';
    } else if (score <= -35) {
        color = 'text-yellow-700'; // Icon color (brownish), background will be the icon shape
        textColor = 'text-white';  // For score text on top of icon
        badgeText = 'TURD';
        iconType = 'poop';
    }
    
    // Ensure text color for non-icon states with specific backgrounds
    if (!iconType && (color === 'bg-green-500' || color === 'bg-red-500')) {
        textColor = 'text-white';
    }


    return { score, color, textColor, details, badgeText, iconType };
};

// Helper function: renderGradientBar (Keep as is)
const renderGradientBar = (title, segments, gradientCss) => (
      <div className="flex flex-col items-start space-y-1 w-full">
          <h3 className="text-xs font-semibold text-base-content uppercase tracking-wider">{title}</h3>
          <div className="w-full h-3 rounded-sm shadow-inner bg-base-300" title={`${title} Distribution`}>
              <div className="h-full rounded-sm" style={{ background: gradientCss }} />
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 w-full">
              {segments.map(segment => (
                  <div key={segment.key} className="flex items-center gap-1.5 text-xs text-base-content/80">
                      {/* Use inline style for background color based on hex */}
                      <span
                         className={`w-2.5 h-2.5 rounded-sm`}
                         style={{ backgroundColor: generateGradient([segment]).match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/)?.[0] || '#a1a1aa' }} // Extract hex for swatch
                       />
                      <span>{segment.key} <span className='opacity-70'>({segment.percentage.toFixed(0)}%)</span></span>
                  </div>
              ))}
          </div>
      </div>
);


// --- Main StatsBar Component ---
const StatsBar = ({ snippets, className }) => { // Accept className prop
    // Add null/undefined check for snippets
    const validSnippets = snippets || [];
    const volume = validSnippets.length;

    // --- Calculate Distributions (Ensure robust handling of missing keys/data) ---
    const calculateDistribution = (keyField) => {
        const distribution = validSnippets.reduce((acc, snippet) => {
            const key = snippet[keyField] || 'Unknown'; // Use 'Unknown' for null/undefined keys
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
        // Ensure all expected keys exist, even if count is 0
        const expectedKeys = keyField === 'classification'
            ? ['Performance', 'Waiver', 'Injury', 'Trade', 'Rankings', 'Neutral'] // Added Neutral
            : keyField === 'value'
                ? ['High', 'Medium', 'Low']
                : keyField === 'sentiment'
                    ? ['Positive', 'Negative', 'Neutral'] // Added Neutral
                    : [];
        expectedKeys.forEach(key => { if (!(key in distribution)) distribution[key] = 0; });
        // Add 'Unknown' only if it actually occurred and isn't expected
        if (distribution.Unknown > 0 && !expectedKeys.includes('Unknown')) {
            expectedKeys.push('Unknown');
        }
        return { distribution, allKeys: expectedKeys };
    };

    const { distribution: classificationDistribution, allKeys: allClassifications } = calculateDistribution('classification');
    const classificationColors = { Performance: 'from-green-400 to-green-600', Waiver: 'from-blue-400 to-blue-600', Injury: 'from-red-400 to-red-600', Trade: 'from-yellow-300 to-yellow-500', Rankings: 'from-purple-400 to-purple-600', Neutral: 'from-gray-400 to-gray-500', Unknown: 'from-gray-400 to-gray-600', };
    const classificationSegments = volume > 0 ? allClassifications.map(key => ({ key, count: classificationDistribution[key], percentage: (classificationDistribution[key] / volume) * 100, color: classificationColors[key] || classificationColors.Unknown, })).filter(segment => segment.count > 0).sort((a, b) => b.percentage - a.percentage) : [];
    const classificationGradient = generateGradient(classificationSegments);

    const { distribution: valueDistribution, allKeys: allValues } = calculateDistribution('value');
    const valueColors = { High: 'from-emerald-400 to-emerald-600', Medium: 'from-indigo-400 to-indigo-600', Low: 'from-rose-400 to-rose-600', Unknown: 'from-gray-400 to-gray-600', };
    const valueOrder = { 'High': 1, 'Medium': 2, 'Low': 3, 'Unknown': 4 };
    const valueSegments = volume > 0 ? allValues.map(key => ({ key, count: valueDistribution[key], percentage: (valueDistribution[key] / volume) * 100, color: valueColors[key] || valueColors.Unknown, })).filter(segment => segment.count > 0).sort((a, b) => valueOrder[a.key] - valueOrder[b.key]) : [];
    const valueGradient = generateGradient(valueSegments);

    const { distribution: sentimentDistribution } = calculateDistribution('sentiment');
    const npsInfo = calculateNpsInfo(sentimentDistribution, volume);

    // --- Volume Styling & Icon --- Logic Reimagined for distinct shapes ---
    let volumeDisplayElement;
    let volumeBadgeText = null;
    let volumeBadgeClass = '';

    if (volume > 50) { // HOT - Flame Shape
        volumeBadgeText = 'HOT';
        volumeBadgeClass = 'bg-orange-500 text-white';
        volumeDisplayElement = (
            <>
                <FlameIcon className="w-full h-full text-orange-500" />
                <span className="absolute font-bold text-xl text-white select-none pointer-events-none">{volume}</span>
            </>
        );
    } else if (volume < 5) { // COLD - Ice Cube Style
        volumeBadgeText = 'COLD';
        volumeBadgeClass = 'badge-info';
        volumeDisplayElement = (
            <div className="w-full h-full rounded-lg bg-sky-400 flex items-center justify-center shadow-md">
                <span className="font-bold text-xl text-sky-900 select-none pointer-events-none">{volume}</span>
            </div>
        );
    } else { // Neutral (5-50) - Black Circle
        volumeDisplayElement = (
            <div className="w-full h-full rounded-full bg-black flex items-center justify-center shadow-md">
                <span className="font-bold text-xl text-white select-none pointer-events-none">{volume}</span>
            </div>
        );
    }

    return (
        // --- Root Element Modification ---
        <div className={`flex flex-col gap-y-4 p-4 bg-base-100 rounded-xl shadow-lg h-full justify-around ${className || ''}`}>

            {/* Row 1: Volume & Sentiment */}
            <div className="flex flex-row gap-x-4 sm:gap-x-8 justify-center items-center">
                {/* Volume Display */}
                <div className="flex flex-col items-center space-y-1">
                    <h3 className="text-xs font-semibold text-base-content uppercase tracking-wider mb-1">Volume</h3>
                    <div className="w-16 h-16 relative flex items-center justify-center" title={`Total Snippets: ${volume}`}>
                        {volumeDisplayElement}
                        {volumeBadgeText && (
                            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 z-10"> {/* translate-y-1/2 to push half its height down */}
                                <span className={`badge badge-sm ${volumeBadgeClass}`}>{volumeBadgeText}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sentiment Display */}
                <div className="flex flex-col items-center space-y-1">
                    <h3 className="text-xs font-semibold text-base-content uppercase tracking-wider mb-1">Sentiment</h3>
                    <div className="w-16 h-16 flex items-center justify-center relative" title={`Sentiment Score (NPS-like). ${npsInfo.details}`}>
                        {npsInfo.iconType === 'heart' && (
                            <HeartIcon className={`w-full h-full ${npsInfo.color}`} />
                        )}
                        {npsInfo.iconType === 'poop' && (
                            <PoopIcon className={`w-14 h-14 ${npsInfo.color}`} /> 
                        )}
                        {!npsInfo.iconType && (
                            <div className={`w-full h-full rounded-full flex items-center justify-center shadow-md ${npsInfo.color}`}>
                                <span className={`text-xl font-bold ${npsInfo.textColor}`}>{npsInfo.score}</span>
                            </div>
                        )}
                        {(npsInfo.iconType === 'heart' || npsInfo.iconType === 'poop') && (
                            <span className={`absolute text-xl font-bold ${npsInfo.textColor}`}>{npsInfo.score}</span>
                        )}
                        {npsInfo.badgeText && (
                            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 z-10">
                                 <span className={`badge badge-sm ${npsInfo.score > 20 ? 'badge-secondary' : 'badge-warning'}`}>{npsInfo.badgeText}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Row 2: Classification Bar */}
            {renderGradientBar("Classification", classificationSegments, classificationGradient)}

            {/* Row 3: Value Bar */}
            {renderGradientBar("Value", valueSegments, valueGradient)}

        </div>
    );
};

export default StatsBar;